import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterRenderEffect,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { ContextMenuItem, ContextMenuService } from '@shared/context-menu/context-menu.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { ViewportService } from '@core/services/viewport.service';

/** Viewmodel per tab; labels worden hier afgeleid (titellogica spec §7). */
export interface HdChartTabBarItem {
  readonly id: string;
  readonly presetName: string | null;
  readonly presetModified: boolean;
  readonly gameMode: boolean;
  /** Optionele hover-tooltip op de tab (bv. volledige datum/tijd voor Just Now). */
  readonly presetTooltip?: string | null;
  /** Onopgeslagen chart in deze tab (preset zonder savedChartId) → sterretje. */
  readonly unsaved?: boolean;
  /** Gedeelde chart, niet in de eigen library (spec 2026-10-20-chart-share) → deel-icoon i.p.v. sterretje. */
  readonly shared?: boolean;
  /** Er wordt voor deze tab nog data opgehaald zonder dat er iets te tonen is → dunne laadlijn. */
  readonly loading?: boolean;
  /** Berekende chart met planeetactivaties → "PDF downloaden" in het contextmenu. */
  readonly printable?: boolean;
}

const ARROW_SCROLL_STEP_PX = 160;
/** Inhouden vóór de drag-highlight verschijnt. */
const PRESS_HOLD_MS = 150;

@Component({
  selector: 'hd-chart-tab-bar',
  imports: [SharedModule, CdkDropList, CdkDrag],
  templateUrl: './hd-chart-tab-bar.component.html',
  styleUrl: './hd-chart-tab-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartTabBarComponent {
  readonly tabs = input.required<readonly HdChartTabBarItem[]>();
  readonly activeTabId = input.required<string>();
  readonly tabSelected = output<string>();
  readonly tabCloseRequested = output<string>();
  readonly addRequested = output<void>();
  readonly tabReordered = output<{ previousIndex: number; currentIndex: number }>();
  readonly closeAllTabsRequested = output<void>();
  readonly closeTabsToLeftRequested = output<string>();
  readonly closeTabsToRightRequested = output<string>();
  /** Context-menu "Chart herberekenen" voor de aangeklikte tab. */
  /** Tab-context-menu "Chart herladen": herstel de geladen chart zonder herberekening. */
  readonly reloadRequested = output<string>();
  readonly recalculateRequested = output<string>();
  /** Context-menu "Chart opslaan" voor de aangeklikte tab (enkel bij onopgeslagen werk). */
  readonly saveChartRequested = output<string>();
  /** Context-menu "Chart hernoemen" voor de aangeklikte tab. */
  readonly renameRequested = output<string>();
  /**
   * Tab waarvoor "Moment bewaren" beschikbaar is (de actieve tab met een
   * actieve just-now overlay op een opgeslagen chart); null = nergens.
   */
  readonly momentSaveTabId = input<string | null>(null);
  /** Context-menu "Moment bewaren" voor de aangeklikte tab (spec notable moments §4.2). */
  readonly momentSaveRequested = output<string>();
  /** Context-menu "PDF downloaden…" voor de aangeklikte tab: opent de printdialoog (spec chart-pdf-print). */
  readonly pdfDownloadRequested = output<string>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly contextMenu = inject(ContextMenuService);
  /** Touch: tab-herordening via long-press-drag vecht met horizontaal scrollen — daar uit. */
  protected readonly viewport = inject(ViewportService);
  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);
  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');

  /** Pijltjes alleen tonen wanneer de tabs effectief overflowen. */
  protected readonly canScroll = signal(false);

  /** Tab die ingedrukt-gehouden wordt: krijgt de "je kan slepen"-highlight. */
  protected readonly pressedTabId = signal<string | null>(null);
  private pressTimerId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Overflow-detectie na elke render (tabs kunnen bredere/smallere set worden).
    afterRenderEffect(() => {
      this.tabs();
      this.measureCanScroll();
    });

    // Actieve tab in beeld scrollen (na render, zodat de DOM up-to-date is).
    afterRenderEffect(() => {
      const activeId = this.activeTabId();
      const el = this.scroller()?.nativeElement;
      el?.querySelector(`[data-tab-id="${CSS.escape(activeId)}"]`)
        ?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
    });
  }

  protected t(key: string): string {
    return this.transloco.translate(key, undefined, this.lang());
  }

  protected tabLabel(tab: HdChartTabBarItem): string {
    if (tab.gameMode) {
      return this.t('humanDesign.chart.controls.gameModeTab');
    }
    if (tab.presetName) {
      const base = tab.presetModified ? `${tab.presetName} •` : tab.presetName;
      // Een gedeelde tab draagt het deel-icoon (template) in plaats van het sterretje.
      return tab.unsaved && !tab.shared ? `${base} *` : base;
    }
    return this.t('humanDesign.chart.controls.freeMode');
  }

  /** Verticale wheel-delta wordt horizontale tabscroll (spec §7). */
  protected onWheel(event: WheelEvent): void {
    const el = this.scroller()?.nativeElement;
    if (!el || el.scrollWidth <= el.clientWidth) {
      return;
    }
    event.preventDefault();
    el.scrollLeft += event.deltaY !== 0 ? event.deltaY : event.deltaX;
  }

  protected scrollByDirection(direction: -1 | 1): void {
    this.scroller()?.nativeElement.scrollBy({ left: direction * ARROW_SCROLL_STEP_PX, behavior: 'smooth' });
  }

  /**
   * Bepaalt of de pijltjes nodig zijn. Bewust níet via `scrollWidth`: tijdens
   * een reorder vervuilt CDK die met een (absoluut-gepositioneerde) preview en
   * een via `transform` animerend item. We tellen daarom de layout-breedtes
   * (`offsetWidth` negeert transforms) van de échte tabs op, met correctie voor
   * de 1px-randoverlap (`.tab + .tab { margin-left: -1px }`), zodat de meting
   * ook midden in een drop klopt.
   */
  private measureCanScroll(): void {
    const el = this.scroller()?.nativeElement;
    if (!el) {
      return;
    }
    const tabs = el.querySelectorAll<HTMLElement>('.tab:not(.cdk-drag-preview):not(.cdk-drag-placeholder)');
    let contentWidth = 0;
    tabs.forEach(tab => (contentWidth += tab.offsetWidth));
    contentWidth -= Math.max(0, tabs.length - 1);
    this.canScroll.set(contentWidth > el.clientWidth + 1);
  }

  protected onCloseClick(event: MouseEvent, tabId: string): void {
    event.stopPropagation();
    this.tabCloseRequested.emit(tabId);
  }

  // ── Drag & drop reorder ─────────────────────────────────────────────────

  /** Start de "ingedrukt-houden"-timer die de drag-highlight aanzet. */
  protected onTabPointerDown(event: PointerEvent, tabId: string): void {
    // Enkel primaire knop; niet op de sluitknop (die heeft een eigen actie).
    if (event.button !== 0 || (event.target as HTMLElement).closest('.tab-close')) {
      return;
    }
    this.clearPressTimer();
    this.pressTimerId = setTimeout(() => this.pressedTabId.set(tabId), PRESS_HOLD_MS);
  }

  protected onTabPointerUp(): void {
    this.clearPressTimer();
    this.pressedTabId.set(null);
  }

  protected onDragStarted(): void {
    // Vanaf hier neemt de CDK drag-preview de visuele cue over.
    this.clearPressTimer();
    this.pressedTabId.set(null);
  }

  protected onDrop(event: CdkDragDrop<readonly HdChartTabBarItem[]>): void {
    this.clearPressTimer();
    this.pressedTabId.set(null);
    if (event.previousIndex !== event.currentIndex) {
      this.tabReordered.emit({ previousIndex: event.previousIndex, currentIndex: event.currentIndex });
    }
  }

  private clearPressTimer(): void {
    if (this.pressTimerId != null) {
      clearTimeout(this.pressTimerId);
      this.pressTimerId = null;
    }
  }

  // ── Context-menu (bulk sluiten) ─────────────────────────────────────────

  protected onTabContextMenu(event: MouseEvent, tabId: string): void {
    event.preventDefault();
    const tabs = this.tabs();
    const index = tabs.findIndex(tab => tab.id === tabId);
    const items: ContextMenuItem[] = [];
    // Opslaan is de primaire actie: als eerste item, enkel bij onopgeslagen werk.
    if (tabs[index]?.unsaved) {
      items.push({
        label: this.t('humanDesign.chart.controls.saveChart'),
        icon: 'floppy-disk',
        action: () => this.saveChartRequested.emit(tabId),
      });
    }
    // Moment bewaren: enkel op de tab met de actieve just-now overlay
    // (spec notable moments §4.2) — de page bepaalt de beschikbaarheid.
    if (tabs[index]?.id === this.momentSaveTabId()) {
      items.push({
        label: this.t('humanDesign.chart.controls.saveMoment'),
        icon: 'calendar-star',
        action: () => this.momentSaveRequested.emit(tabId),
      });
    }
    if (tabs[index]?.presetName) {
      items.push({
        label: this.t('humanDesign.chart.controls.renameChart'),
        icon: 'pen',
        action: () => this.renameRequested.emit(tabId),
      });
      // Herladen boven herberekenen: edits terugdraaien zonder backend-call.
      items.push({
        label: this.t('humanDesign.chart.controls.reloadChart'),
        icon: 'undo',
        action: () => this.reloadRequested.emit(tabId),
      });
      items.push({
        label: this.t('humanDesign.chart.controls.recalculateChart'),
        icon: 'sync-alt',
        action: () => this.recalculateRequested.emit(tabId),
      });
    }
    if (tabs[index]?.printable) {
      items.push({
        label: this.t('humanDesign.chart.controls.downloadPdf'),
        icon: 'file-pdf',
        action: () => this.pdfDownloadRequested.emit(tabId),
      });
    }
    items.push({
      label: this.t('humanDesign.chart.controls.closeAllTabs'),
      icon: 'xmark',
      action: () => this.closeAllTabsRequested.emit(),
    });
    if (index > 0) {
      items.push({
        label: this.t('humanDesign.chart.controls.closeTabsToLeft'),
        icon: 'angles-left',
        action: () => this.closeTabsToLeftRequested.emit(tabId),
      });
    }
    if (index < tabs.length - 1) {
      items.push({
        label: this.t('humanDesign.chart.controls.closeTabsToRight'),
        icon: 'angles-right',
        action: () => this.closeTabsToRightRequested.emit(tabId),
      });
    }
    this.contextMenu.open({ x: event.clientX, y: event.clientY }, items);
  }
}
